import { inject, Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction } from '@mediahaven/shared';
import {
    ApiFragment,
    Mh2Entity,
    NavigationLink,
    RecordType,
    RecordTypesService,
    RecordTypeTranslationService
} from '@stylehaven/components';
import { EMPTY, Observable, of } from 'rxjs';
import { mapTo, switchMap, take } from 'rxjs/operators';
import { SelectParentDialogType } from '@common/entities/dialog/select-parent-dialog.entity';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';
import { MediaHavenActionType } from '../../../entities/action-types.entity';
import { ActionService } from '../../abstract/action.service.interface';

@Injectable({ providedIn: 'root' })
export class CoupleToParentActionService extends ActionService {
    protected readonly action: MediaHavenActionType = MediaHavenActionType.COUPLE_TO_PARENT;

    // Services
    private readonly recordsSandbox: RecordsSandbox = inject(RecordsSandbox);
    private readonly recordTypeService: RecordTypesService = inject(RecordTypesService);
    private readonly recordTypeTranslationService: RecordTypeTranslationService = inject(RecordTypeTranslationService);
    private readonly dialogHelper: DialogHelper = inject(DialogHelper);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Couple to parent
     * Available when:
     *  * User has edit rights
     *  * Is not already a child object
     *  * Has possible parent types
     *  * Is not a candidate child
     *  * Is not deleted
     *  * Is not a representation
     *
     * Disabled when:
     *  * Has non-editable status
     *  * Item has errors
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return of(this.userActionBuilder(record));
    }

    public userActionBuilder(record: ApiFragment): UserAction {
        const hasEditRights: boolean = record.hasEditRights();
        const hasParent: boolean = record.hasParent();
        const hasPossibleParent: boolean = this.recordTypeService.getParentsOfType(record.getRecordType()).length > 0;
        const isCandidateChild: boolean = record.isCandidateChild();
        const isDeleted: boolean = record.isDeleted();
        const isRepresentation: boolean = record.getRecordType() === RecordType.Representation;

        const hasEditableStatus: boolean = record.hasEditableStatus();
        const hasErrors: boolean = record.hasSevereErrors() ?? false;

        const availableConditions: boolean[] = [
            hasEditRights,
            !hasParent,
            hasPossibleParent,
            !isCandidateChild,
            !isDeleted,
            !isRepresentation
        ];
        const disableConditions: ActionDisabledCondition[] = [
            { condition: !hasEditableStatus, tooltip: 'general.blocked-status' },
            { condition: hasErrors, tooltip: 'general.blocked-status' }
        ];

        return new UserAction(availableConditions, disableConditions);
    }

    protected onAction(target: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        return this.dialogHelper
            .selectParentDialog(
                SelectParentDialogType.ADD,
                [target.getRecordType()],
                target.getClassificationId() ?? undefined,
                [target.getRecordId()]
            )
            .pipe(
                switchMap(
                    output =>
                        output?.parentRecord
                            ? this.addRecordToParent(target, output.parentRecord, actionItemOptions)
                            : EMPTY // Canceling should not lead to an error, but should complete the stream
                ),
                take(1)
            );
    }

    private addRecordToParent(
        record: ApiFragment,
        parent: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Observable<boolean> {
        actionItemOptions.parent = parent;
        this.initBusyToast(record, actionItemOptions, true);

        return this.recordsSandbox.addRecordToParent(record, parent).pipe(mapTo(true));
    }

    public getLabelKey(target: ApiFragment): string {
        return 'actions.intellectual.add';
    }

    public getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.add-to-intellectual.add';
    }

    public getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public getSuccessToastLink(target: ApiFragment, actionItemOptions: ActionItemOptions): NavigationLink | undefined {
        return {
            route: actionItemOptions.parent!.getContentUrl()
        };
    }

    public getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return 'toasts.see.intellectual';
    }

    public getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {
            parentType: this.recordTypeTranslationService.getRecordTypeTranslation(
                actionItemOptions.parent!.getRecordType()
            )
        };
    }
}
